// src/components/admin/page-sections/SlideshowHeaderWaveSection.js
import React, { useState, useEffect } from 'react';
import { Phone } from 'lucide-react';
import LazyImage from '../../LazyImage';

const SlideshowHeaderWaveSection = ({ content }) => {
  const {
    title = 'Paper Bag & Box <span>Custom Packaging</span>',
    subtitle = 'Elevate your brand with premium quality custom bags and boxes. Perfect for businesses of all sizes.',
    backgroundImage = '/images/s1.jpg',
    mobileBackgroundImage = '/images/s3.jpg',
    overlayColor = 'rgba(0,0,0,0.5)',
    primaryButtonText = 'Customize Products',
    primaryButtonLink = '/products',
    primaryButtonColor = '#d97706', // yellow-600
    secondaryButtonText = 'Call to Order',
    secondaryButtonLink = 'tel:+18333333101',
    secondaryButtonColor = '#16a34a', // green-600
    phoneNumber = '+18333333101',
    showPhoneIcon = true,
    showWaveSeparator = true,
    separatorColor = '#fbfbfb',
    particlesCount = 15,
    particlesColor = 'rgba(255, 255, 255, 0.2)',
    highlightColor = '#f59e0b', // amber-500
    height = '100vh',
    minHeight = '600px',
    animationEnabled = true,
    // Video background properties
    videoBackground = false,
    backgroundVideo = '',
    mobileBackgroundVideo = '',
    videoLoop = true,
    videoMuted = true,
    videoAutoplay = true,
    videoPoster = ''
  } = content || {};

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Update isMobile state on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Parse HTML in title string to allow for highlights
  const renderTitle = () => {
    if (!title) return null;
    
    const parts = title.split(/<span>(.*?)<\/span>/);
    if (parts.length === 1) return <span>{title}</span>;
    
    return (
      <>
        {parts[0]}
        <span className="text-amber-500 relative" style={{ color: highlightColor }}>{parts[1]}</span>
        {parts[2] || ''}
      </>
    );
  };

  // Get video source based on device
  const getVideoSource = () => {
    if (isMobile && mobileBackgroundVideo) {
      return mobileBackgroundVideo;
    }
    return backgroundVideo;
  };

  // Get background styles for image mode
  const getBackgroundStyles = () => {
    return {
      backgroundImage: `url('${isMobile ? mobileBackgroundImage : backgroundImage}')`,
      backgroundBlendMode: 'overlay',
      backgroundColor: overlayColor,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      height: height,
      minHeight: minHeight
    };
  };

  return (
    <div className="relative overflow-hidden" style={{ height: height, minHeight: minHeight }}>
      {/* Background: Either Video or Image */}
      {videoBackground ? (
        <>
          {/* Video Background */}
          <div className="absolute inset-0 z-0">
            <video
              className="object-cover h-full w-full"
              autoPlay={videoAutoplay}
              loop={videoLoop}
              muted={videoMuted}
              playsInline
              poster={videoPoster}
              src={getVideoSource()}
              style={{
                backgroundColor: overlayColor // As fallback background color
              }}
            >
              Your browser does not support the video tag.
            </video>
            <div 
              className="absolute inset-0"
              style={{ backgroundColor: overlayColor }}
            ></div>
          </div>
        </>
      ) : (
        <>
          {/* Image Background */}
          <div 
            className="absolute inset-0 bg-cover bg-center z-0" 
            style={getBackgroundStyles()}
          ></div>
        </>
      )}

      {/* Animated gradient overlay - works with both video and image */}
      <div 
        className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/70 z-1"
        style={{
          animation: animationEnabled ? 'pulseGradient 8s ease-in-out infinite' : 'none'
        }}
      ></div>
      
      {/* Animated floating particles */}
      {particlesCount > 0 && (
        <div className="absolute inset-0 overflow-hidden z-2">
          {[...Array(parseInt(particlesCount, 10))].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full"
              style={{
                width: `${Math.random() * 6 + 2}px`,
                height: `${Math.random() * 6 + 2}px`,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                backgroundColor: particlesColor,
                animation: animationEnabled ? `floatParticle ${Math.random() * 15 + 10}s linear infinite` : 'none',
                animationDelay: animationEnabled ? `${Math.random() * 5}s` : '0s'
              }}
            ></div>
          ))}
        </div>
      )}
      
      {/* Content */}
      <div className="container mx-auto px-4 h-full flex items-center relative z-10">
        <div className="w-full max-w-4xl mx-auto text-center text-white">
          <h1 
            className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold mb-6 leading-tight opacity-0"
            style={{
              animation: animationEnabled ? 'fadeSlideUp 0.8s ease-out forwards' : 'none'
            }}
          >
            {renderTitle()}
          </h1>
          <p 
            className="text-xl md:text-2xl mb-10 max-w-3xl mx-auto text-gray-200 leading-relaxed opacity-0"
            style={{
              animation: animationEnabled ? 'fadeSlideUp 0.8s ease-out forwards 0.3s' : 'none'
            }}
          >
            {subtitle}
          </p>
          <div 
            className="flex flex-col sm:flex-row justify-center gap-6"
          >
            {primaryButtonText && (
              <button
                onClick={() => window.location.href = primaryButtonLink}
                className="text-white px-10 py-4 rounded-lg text-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg opacity-0"
                style={{
                  backgroundColor: primaryButtonColor,
                  animation: animationEnabled ? 'fadeSlideUp 0.8s ease-out forwards 0.6s' : 'none'
                }}
              >
                <span className="relative z-10">{primaryButtonText}</span>
                <span className={`absolute inset-0 rounded-lg bg-gradient-to-r opacity-0 hover:opacity-100 transition-opacity duration-300`} 
                  style={{
                    backgroundImage: `linear-gradient(to right, ${primaryButtonColor}, ${adjustColor(primaryButtonColor, 20)})`
                  }}
                ></span>
              </button>
            )}
            
            {secondaryButtonText && (
              <a
                href={secondaryButtonLink}
                className="text-white px-10 py-4 rounded-lg text-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center gap-2 opacity-0"
                style={{
                  backgroundColor: secondaryButtonColor,
                  animation: animationEnabled ? 'fadeSlideUp 0.8s ease-out forwards 0.8s' : 'none'
                }}
              >
                {showPhoneIcon && <Phone size={20} className="animate-pulse" />}
                <span>{secondaryButtonText}</span>
              </a>
            )}
          </div>
        </div>
      </div>
      
      {/* Wave Separator */}
      {showWaveSeparator && (
        <div className="absolute bottom-0 left-0 right-0 z-10">
          <svg 
            viewBox="0 0 1440 200" 
            xmlns="http://www.w3.org/2000/svg" 
            className="w-full h-auto"
            preserveAspectRatio="none"
          >
            <path 
              d="M0,128L80,144C160,160,320,192,480,186.7C640,181,800,139,960,128C1120,117,1280,139,1360,149.3L1440,160L1440,320L1360,320C1280,320,1120,320,960,320C800,320,640,320,480,320C320,320,160,320,80,320L0,320Z" 
              fill={separatorColor}
            />
          </svg>
        </div>
      )}
      
      {/* CSS animations */}
      <style jsx="true">{`
        @keyframes fadeSlideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes pulseGradient {
          0%, 100% {
            opacity: 0.8;
          }
          50% {
            opacity: 0.5;
          }
        }
        
        @keyframes floatParticle {
          0% {
            transform: translateY(0) translateX(0);
            opacity: 0;
          }
          10% {
            opacity: 0.8;
          }
          90% {
            opacity: 0.8;
          }
          100% {
            transform: translateY(-100vh) translateX(20px);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

// Helper function to adjust color brightness
function adjustColor(color, percent) {
  // Convert hex to RGB
  let r, g, b;
  if (color.startsWith('#')) {
    r = parseInt(color.substring(1, 3), 16);
    g = parseInt(color.substring(3, 5), 16);
    b = parseInt(color.substring(5, 7), 16);
  } else if (color.startsWith('rgb')) {
    const matches = color.match(/\d+/g);
    if (matches && matches.length >= 3) {
      r = parseInt(matches[0]);
      g = parseInt(matches[1]);
      b = parseInt(matches[2]);
    } else {
      return color; // Return original if can't parse
    }
  } else {
    return color; // Return original if format not recognized
  }

  // Adjust each color component
  r = Math.min(255, Math.max(0, r + (percent / 100) * 255));
  g = Math.min(255, Math.max(0, g + (percent / 100) * 255));
  b = Math.min(255, Math.max(0, b + (percent / 100) * 255));

  // Convert back to hex
  return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
}

export default SlideshowHeaderWaveSection;








// // src/components/admin/page-sections/SlideshowHeaderWaveSection.js
// import React from 'react';
// import { Phone } from 'lucide-react';
// import LazyImage from '../../LazyImage';

// const SlideshowHeaderWaveSection = ({ content }) => {
//   const {
//     title = 'Paper Bag & Box <span>Custom Packaging</span>',
//     subtitle = 'Elevate your brand with premium quality custom bags and boxes. Perfect for businesses of all sizes.',
//     backgroundImage = '/images/s1.jpg',
//     mobileBackgroundImage = '/images/s3.jpg',
//     overlayColor = 'rgba(0,0,0,0.5)',
//     primaryButtonText = 'Customize Products',
//     primaryButtonLink = '/products',
//     primaryButtonColor = '#d97706', // yellow-600
//     secondaryButtonText = 'Call to Order',
//     secondaryButtonLink = 'tel:+18333333101',
//     secondaryButtonColor = '#16a34a', // green-600
//     phoneNumber = '+18333333101',
//     showPhoneIcon = true,
//     showWaveSeparator = true,
//     separatorColor = '#fbfbfb',
//     particlesCount = 15,
//     particlesColor = 'rgba(255, 255, 255, 0.2)',
//     highlightColor = '#f59e0b', // amber-500
//     height = '100vh',
//     minHeight = '600px',
//     animationEnabled = true
//   } = content;

//   // Parse HTML in title string to allow for highlights
//   const renderTitle = () => {
//     if (!title) return null;
    
//     const parts = title.split(/<span>(.*?)<\/span>/);
//     if (parts.length === 1) return <span>{title}</span>;
    
//     return (
//       <>
//         {parts[0]}
//         <span className="text-amber-500 relative">{parts[1]}</span>
//         {parts[2] || ''}
//       </>
//     );
//   };

//   return (
//     <div 
//       className="relative bg-gray-400 bg-cover bg-center mb-0 overflow-hidden" 
//       style={{
//         backgroundImage: `url('${window.innerWidth < 768 ? mobileBackgroundImage : backgroundImage}')`,
//         backgroundBlendMode: 'overlay',
//         backgroundColor: overlayColor,
//         height: height,
//         minHeight: minHeight
//       }}
//     >
//       {/* Animated gradient overlay */}
//       <div 
//         className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/70"
//         style={{
//           animation: animationEnabled ? 'pulseGradient 8s ease-in-out infinite' : 'none'
//         }}
//       ></div>
      
//       {/* Animated floating particles */}
//       {particlesCount > 0 && (
//         <div className="absolute inset-0 overflow-hidden">
//           {[...Array(particlesCount)].map((_, i) => (
//             <div
//               key={i}
//               className="absolute rounded-full"
//               style={{
//                 width: `${Math.random() * 6 + 2}px`,
//                 height: `${Math.random() * 6 + 2}px`,
//                 top: `${Math.random() * 100}%`,
//                 left: `${Math.random() * 100}%`,
//                 backgroundColor: particlesColor,
//                 animation: animationEnabled ? `floatParticle ${Math.random() * 15 + 10}s linear infinite` : 'none',
//                 animationDelay: animationEnabled ? `${Math.random() * 5}s` : '0s'
//               }}
//             ></div>
//           ))}
//         </div>
//       )}
      
//       <div className="container mx-auto px-4 h-full flex items-center relative z-10">
//         <div className="w-full max-w-4xl mx-auto text-center text-white">
//           <h1 
//             className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold mb-6 leading-tight opacity-0"
//             style={{
//               animation: animationEnabled ? 'fadeSlideUp 0.8s ease-out forwards' : 'none'
//             }}
//           >
//             {renderTitle()}
//           </h1>
//           <p 
//             className="text-xl md:text-2xl mb-10 max-w-3xl mx-auto text-gray-200 leading-relaxed opacity-0"
//             style={{
//               animation: animationEnabled ? 'fadeSlideUp 0.8s ease-out forwards 0.3s' : 'none'
//             }}
//           >
//             {subtitle}
//           </p>
//           <div 
//             className="flex flex-col sm:flex-row justify-center gap-6"
//           >
//             {primaryButtonText && (
//               <button
//                 onClick={() => window.location.href = primaryButtonLink}
//                 className="text-white px-10 py-4 rounded-lg text-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg opacity-0"
//                 style={{
//                   backgroundColor: primaryButtonColor,
//                   animation: animationEnabled ? 'fadeSlideUp 0.8s ease-out forwards 0.6s' : 'none'
//                 }}
//               >
//                 <span className="relative z-10">{primaryButtonText}</span>
//                 <span className={`absolute inset-0 rounded-lg bg-gradient-to-r opacity-0 hover:opacity-100 transition-opacity duration-300`} 
//                   style={{
//                     backgroundImage: `linear-gradient(to right, ${primaryButtonColor}, ${adjustColor(primaryButtonColor, 20)})`
//                   }}
//                 ></span>
//               </button>
//             )}
            
//             {secondaryButtonText && (
//               <a
//                 href={secondaryButtonLink}
//                 className="text-white px-10 py-4 rounded-lg text-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center gap-2 opacity-0"
//                 style={{
//                   backgroundColor: secondaryButtonColor,
//                   animation: animationEnabled ? 'fadeSlideUp 0.8s ease-out forwards 0.8s' : 'none'
//                 }}
//               >
//                 {showPhoneIcon && <Phone size={20} className="animate-pulse" />}
//                 <span>{secondaryButtonText}</span>
//               </a>
//             )}
//           </div>
//         </div>
//       </div>
      
//       {showWaveSeparator && (
//         <div className="absolute bottom-0 left-0 right-0">
//           <svg 
//             viewBox="0 0 1440 200" 
//             xmlns="http://www.w3.org/2000/svg" 
//             className="w-full h-auto"
//             preserveAspectRatio="none"
//           >
//             <path 
//               d="M0,128L80,144C160,160,320,192,480,186.7C640,181,800,139,960,128C1120,117,1280,139,1360,149.3L1440,160L1440,320L1360,320C1280,320,1120,320,960,320C800,320,640,320,480,320C320,320,160,320,80,320L0,320Z" 
//               fill={separatorColor}
//             />
//           </svg>
//         </div>
//       )}
      
//       <style jsx>{`
//         @keyframes fadeSlideUp {
//           from {
//             opacity: 0;
//             transform: translateY(30px);
//           }
//           to {
//             opacity: 1;
//             transform: translateY(0);
//           }
//         }
        
//         @keyframes pulseGradient {
//           0%, 100% {
//             opacity: 0.8;
//           }
//           50% {
//             opacity: 0.5;
//           }
//         }
        
//         @keyframes floatParticle {
//           0% {
//             transform: translateY(0) translateX(0);
//             opacity: 0;
//           }
//           10% {
//             opacity: 0.8;
//           }
//           90% {
//             opacity: 0.8;
//           }
//           100% {
//             transform: translateY(-100vh) translateX(20px);
//             opacity: 0;
//           }
//         }
//       `}</style>
//     </div>
//   );
// };

// // Helper function to adjust color brightness
// function adjustColor(color, percent) {
//   // Convert hex to RGB
//   let r, g, b;
//   if (color.startsWith('#')) {
//     r = parseInt(color.substring(1, 3), 16);
//     g = parseInt(color.substring(3, 5), 16);
//     b = parseInt(color.substring(5, 7), 16);
//   } else if (color.startsWith('rgb')) {
//     const matches = color.match(/\d+/g);
//     if (matches && matches.length >= 3) {
//       r = parseInt(matches[0]);
//       g = parseInt(matches[1]);
//       b = parseInt(matches[2]);
//     } else {
//       return color; // Return original if can't parse
//     }
//   } else {
//     return color; // Return original if format not recognized
//   }

//   // Adjust each color component
//   r = Math.min(255, Math.max(0, r + (percent / 100) * 255));
//   g = Math.min(255, Math.max(0, g + (percent / 100) * 255));
//   b = Math.min(255, Math.max(0, b + (percent / 100) * 255));

//   // Convert back to hex
//   return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
// }

// export default SlideshowHeaderWaveSection;